/**
 * @license
 * Copyright 2018 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */

//import { backend_log } from "./kemtai";
import logger from '@kemtai/logger'
//import { ENGINE } from '../tfjs-core/engine';
import { onWebGLContextLost,resetBackend } from '../tfjs-core';


const contexts: {[key: string]: WebGLRenderingContext} = {};

const WEBGL_ATTRIBUTES: WebGLContextAttributes = {
  alpha: false,
  antialias: false,
  premultipliedAlpha: false,
  preserveDrawingBuffer: false,
  depth: false,
  stencil: false,
  //failIfMajorPerformanceCaveat: true
};

export function clearWebGLContext(webGLVersion: number) {
  delete contexts[webGLVersion];
}

export function setWebGLContext(
    webGLVersion: number, gl: WebGLRenderingContext) {
  contexts[webGLVersion] = gl;
}

export function getWebGLContext(webGLVersion: number): WebGLRenderingContext {
  if (!(webGLVersion in contexts)) {
    const newCtx = getWebGLRenderingContext(webGLVersion);
    if (newCtx !== null) {
        contexts[webGLVersion] = newCtx;

        // NEW CONTEXT CREATED ///////////////////////////////////////
        //console.log("------------------------------->>>>> getWebGLContext",webGLVersion)
        // const maxTexSize = newCtx.getParameter(newCtx.MAX_TEXTURE_SIZE);
        // const maxTexUnits = newCtx.getParameter(newCtx.MAX_TEXTURE_IMAGE_UNITS);
        // const maxVertices = newCtx.getParameter(newCtx.MAX_VERTEX_ATTRIBS);
        // const maxVertexUniforms = newCtx.getParameter(newCtx.MAX_VERTEX_UNIFORM_VECTORS);
        // const maxFragmentUniforms = newCtx.getParameter(newCtx.MAX_FRAGMENT_UNIFORM_VECTORS);
        // const maxCombinedTextureImageUnits = newCtx.getParameter(newCtx.MAX_COMBINED_TEXTURE_IMAGE_UNITS);

        // console.log('Max texture size:', maxTexSize);
        // console.log('Max texture units:', maxTexUnits);
        // console.log('Max vertices:', maxVertices);
        // console.log('Max vertex uniforms:', maxVertexUniforms);
        // console.log('Max fragment uniforms:', maxFragmentUniforms);
        // console.log('Max combined texture image units:', maxCombinedTextureImageUnits);
        // console.log("-------------------------------<<<<< getWebGLContext",webGLVersion)
        logger.event('getWebGLContext', {
            webGLVersion: webGLVersion, 
            level:"webgl-log",
            //maxTexSize, maxTexUnits, maxVertices,
            //maxVertexUniforms, maxFragmentUniforms, 
            //maxCombinedTextureImageUnits
        });

        /////////////////////////////////////////////////////////////

    } else {
      console.log('Could not get context for WebGL version', webGLVersion);
      return null;
    }
  }
  const gl = contexts[webGLVersion];
  if (gl.isContextLost()) {
    console.log("------------------->>>>> isContextLost!!!!")

    logger.event('WebGLContextLost', {webGLVersion: webGLVersion, level:"webgl-log"});
    
    delete contexts[webGLVersion];
    return getWebGLContext(webGLVersion);
  }

  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.STENCIL_TEST);
  gl.disable(gl.BLEND);
  gl.disable(gl.DITHER);
  gl.disable(gl.POLYGON_OFFSET_FILL);
  gl.disable(gl.SAMPLE_COVERAGE);
  gl.enable(gl.SCISSOR_TEST);
  gl.enable(gl.CULL_FACE);
  gl.cullFace(gl.BACK);


  return contexts[webGLVersion];
}

function createCanvas(webGLVersion: number) {
  if (typeof OffscreenCanvas !== 'undefined' && webGLVersion === 2) {
    return new OffscreenCanvas(300, 150);
  } else if (typeof document !== 'undefined') {
    return document.createElement('canvas');
  } else {
    throw new Error('Cannot create a canvas in this context');
  }
}



function getWebGLRenderingContext(webGLVersion: number): WebGLRenderingContext {
  if (webGLVersion !== 1 && webGLVersion !== 2) {
    throw new Error('Cannot get WebGL rendering context, WebGL is disabled.');
  }
  const canvas = createCanvas(webGLVersion);

  canvas.addEventListener('webglcontextlost',  (ev: Event) => {
    console.log(`%ctfjs: webglcontextlost`, "color: blue;");
    logger.event('WebGLContextLost-RestartEngine', {level:"webgl-log"});    
    onWebGLContextLost()
    resetBackend()
   
  }, false);
  


  if (webGLVersion === 1) {
    return (canvas.getContext('webgl', WEBGL_ATTRIBUTES) ||
            (canvas as any).getContext('experimental-webgl', WEBGL_ATTRIBUTES)) as
        WebGLRenderingContext;
  }
  return canvas.getContext('webgl2', WEBGL_ATTRIBUTES) as WebGLRenderingContext;
}

/*
function _debugloseContext() {
    console.log('>>> _debugloseContext called');
    const gl = getWebGLContext(2);
    if (gl) {
        // Simulate a context loss
        const loseContext = gl.getExtension('WEBGL_lose_context');
        if (loseContext) {
            loseContext.loseContext();
            console.log('WebGL context lost simulated.');
        } else {
            console.log('WEBGL_lose_context extension not available.');
        }
    } else {
        console.log('No WebGL context available to lose.');
    }
    
    // setTimeout(() => {
    //     _debugloseContext();
    // }
    // , 15000);
    
}


function initDebug() {
    // call _debugloseContext() after 10 seconds
    console.log('>>> _debugloseContext scheduled to run in 10 seconds');

    setTimeout(() => {
        _debugloseContext();
    }
    , 20000);
}

initDebug()
*/